import React, { useEffect, useMemo, useRef, useState } from "react";
import Joyride, { CallBackProps, StoreHelpers } from "react-joyride";
import { CustomStep, getWarehouseJoyrideSteps } from "./warehouseJoyrideSteps";
import { handleWarehouseJoyrideCallback } from "./warehouseJoyrideCallbackHandler";
import { useNavigate, useParams } from "react-router-dom";
import { LOCAL_STORAGE_ACTIVE, LOCAL_STORAGE_STEP, LOCAL_STORAGE_TYPE } from "./constants";
import { WarehouseTutorialType } from "../../../utils/enums";

import { getCurrentStepFromLocalStorage } from "./joyrideStepHelpers";
import { pickHeadroom } from "./utils/pickHeadroom";
import { useStopTutorialOnPop } from "./utils/useStopTutorialOnPop";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";

interface WarehouseJoyrideWrapperProps {
    tutorialType: WarehouseTutorialType;
    startStep?: number;
    trigger?: number;
    hasSelectedServices?: boolean;
    hasProducts?: boolean;
    goNext?: () => void;
}

const WarehouseJoyrideWrapper: React.FC<WarehouseJoyrideWrapperProps> = ({
    tutorialType,
    startStep,
    trigger,
    hasSelectedServices,
    hasProducts,
    goNext,
}) => {
    const navigate = useNavigate();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const [run, setRun] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [joyrideReady, setJoyrideReady] = useState(false);
    const helpersRef = useRef<StoreHelpers | null>(null);

    useStopTutorialOnPop({
        warehouseTutorialActive: run,
        onAbort: () => {
            helpersRef.current?.close();
            setRun(false);
            setStepIndex(0);
        },
    });

    useEffect(() => {
        const handleStepChange = () => {
            if (helpersRef.current) {
                helpersRef.current.close(); // скрыть тултип текущего шага
            }
            setStepIndex(getCurrentStepFromLocalStorage());
        };

        window.addEventListener("warehouseStepChanged", handleStepChange);

        return () => window.removeEventListener("warehouseStepChanged", handleStepChange);
    }, []);

    useEffect(() => {
        const savedActive = localStorage.getItem(LOCAL_STORAGE_ACTIVE) === "true";
        const savedStep = Number(localStorage.getItem(LOCAL_STORAGE_STEP) ?? 0);
        const savedType = localStorage.getItem(LOCAL_STORAGE_TYPE) as WarehouseTutorialType;
        if (savedActive && savedType === tutorialType) {
            setRun(true);
            setStepIndex(savedStep);
        }
    }, [tutorialType]);

    // start tutorial by clicking on button
    useEffect(() => {
        if (startStep !== undefined) {
            setRun(true);
            setStepIndex(startStep);
            localStorage.setItem(LOCAL_STORAGE_ACTIVE, "true");
            localStorage.setItem(LOCAL_STORAGE_STEP, String(startStep));
            localStorage.setItem(LOCAL_STORAGE_TYPE, tutorialType);
        }
    }, [startStep, trigger, tutorialType]);

    const handleCallback = (data: CallBackProps) => {
        handleWarehouseJoyrideCallback(data, setStepIndex, setRun, navigate, steps);
    };

    const steps = useMemo(
        () =>
            getWarehouseJoyrideSteps({
                branchId,
                currentCompanyId,
                tutorialType,
                hasSelectedServices,
                hasProducts,
                goNext,
            }),
        [branchId, currentCompanyId, tutorialType, hasSelectedServices, hasProducts, goNext]
    );

    useEffect(() => {
        if (!run) {
            setJoyrideReady(false);
            return;
        }

        const step = steps?.[stepIndex];
        if (!step) {
            setJoyrideReady(true);
            return;
        }

        const selector = String(step.target || "");
        setJoyrideReady(false);

        const ensureVisibleAndReady = async () => {
            const el = document.querySelector(selector) as HTMLElement | null;

            if (!el) {
                setJoyrideReady(true);
                return;
            }

            const rect = el.getBoundingClientRect();
            const vh = window.innerHeight || document.documentElement.clientHeight;

            if (rect.height <= vh) {
                el.scrollIntoView({ behavior: "auto", block: "center", inline: "center" });
            } else {
                if (rect.top < 0) {
                    window.scrollBy({ top: rect.top - 20, behavior: "auto" });
                }
            }

            await new Promise((res) => requestAnimationFrame(() => requestAnimationFrame(res)));
            setJoyrideReady(true);
            await new Promise((res) => requestAnimationFrame(() => requestAnimationFrame(res)));

            window.dispatchEvent(new Event("resize"));
        };

        ensureVisibleAndReady();
    }, [run, stepIndex, steps]);

    useEffect(() => {
        if (!joyrideReady) return;
        const currentStep: any = steps?.[stepIndex];
        if (!currentStep) return;

        const useObserve = !!currentStep.observeResize || false;
        if (!useObserve) return;

        const selector = String(currentStep.target || "");
        const el = document.querySelector(selector) as HTMLElement | null;
        if (!el) return;

        let rafId: number | null = null;
        const scheduleResize = () => {
            if (rafId != null) return;
            rafId = window.requestAnimationFrame(() => {
                window.dispatchEvent(new Event("resize"));
                rafId = null;
            });
        };

        if ((window as any).ResizeObserver) {
            const ro = new (window as any).ResizeObserver(() => scheduleResize());
            ro.observe(el);
            return () => {
                ro.disconnect();
                if (rafId != null) cancelAnimationFrame(rafId);
            };
        } else {
            const mo = new MutationObserver(() => scheduleResize());
            mo.observe(el, { childList: true, subtree: true, attributes: true, characterData: true });
            return () => {
                mo.disconnect();
                if (rafId != null) cancelAnimationFrame(rafId);
            };
        }
    }, [joyrideReady, stepIndex, steps]);

    // Repositioning the tooltip when scrolling the page (using the pageScrollReposition flag)
    useEffect(() => {
        if (!run || !joyrideReady) return;

        const currentStep = steps?.[stepIndex];
        if (!currentStep?.pageScrollReposition) return;

        let raf = 0;
        let ticking = false;

        const kick = () => {
            if (ticking) return;
            ticking = true;
            raf = requestAnimationFrame(() => {
                window.dispatchEvent(new Event("resize"));
                ticking = false;
            });
        };

        window.addEventListener("scroll", kick, { passive: true });
        window.addEventListener("resize", kick);
        window.visualViewport?.addEventListener("resize", kick);
        window.visualViewport?.addEventListener("scroll", kick);

        return () => {
            window.removeEventListener("scroll", kick);
            window.removeEventListener("resize", kick);
            window.visualViewport?.removeEventListener("resize", kick);
            window.visualViewport?.removeEventListener("scroll", kick);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [run, joyrideReady, stepIndex, steps]);

    // temporarily adding margin-top/bottom for the step target
    useEffect(() => {
        if (!run || !joyrideReady) return;

        const step = steps?.[stepIndex] as CustomStep | undefined;
        if (!step) return;

        const isPageScrollStep = !!step.pageScrollReposition && step.isFixed === false;

        if (isPageScrollStep) return;

        const hasHeadroomMeta =
            (Array.isArray(step.headroomRules) && step.headroomRules.length > 0) ||
            !!step.topHeadroom ||
            !!step.bottomHeadroom;

        if (!hasHeadroomMeta) return;

        const targetSel = String(step.target || "");
        const targetEl = document.querySelector(targetSel) as HTMLElement | null;
        if (!targetEl) return;

        const headroomEl = (step as any).headroomTarget
            ? (document.querySelector((step as any).headroomTarget) as HTMLElement | null)
            : (targetEl.closest(".modal-dialog") as HTMLElement | null) || targetEl;

        if (!headroomEl) return;

        const appliedRef = { top: NaN as number, bottom: NaN as number };

        const applyHeadroom = () => {
            const picked = pickHeadroom(step) ?? { top: 0, bottom: 0 };
            const top = Number(picked.top || 0);
            const bottom = Number(picked.bottom || 0);

            const needUpdate = appliedRef.top !== top || appliedRef.bottom !== bottom;
            if (!needUpdate) return;

            headroomEl.style.marginTop = top ? `${top}px` : "";
            headroomEl.style.marginBottom = bottom ? `${bottom}px` : "";

            appliedRef.top = top;
            appliedRef.bottom = bottom;

            requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
        };

        const prevTop = headroomEl.style.marginTop;
        const prevBottom = headroomEl.style.marginBottom;

        applyHeadroom();

        const onResize = () => applyHeadroom();
        window.addEventListener("resize", onResize);
        window.visualViewport?.addEventListener("resize", onResize);

        return () => {
            headroomEl.style.marginTop = prevTop;
            headroomEl.style.marginBottom = prevBottom;
            window.removeEventListener("resize", onResize);
            window.visualViewport?.removeEventListener("resize", onResize);
            window.dispatchEvent(new Event("resize"));
        };
    }, [run, joyrideReady, stepIndex, steps]);

    useEffect(() => {
        if (!run || !joyrideReady) return;
        const step = steps?.[stepIndex] as CustomStep | undefined;

        const isPageScrollStep = !!step?.pageScrollReposition && step?.isFixed === false;

        if (!isPageScrollStep) return;

        document.body.classList.add("tutorial-unlock-body");

        requestAnimationFrame(() => requestAnimationFrame(() => window.dispatchEvent(new Event("resize"))));

        return () => {
            document.body.classList.remove("tutorial-unlock-body");
            window.dispatchEvent(new Event("resize"));
        };
    }, [run, joyrideReady, stepIndex, steps]);

    const currentStep = steps?.[stepIndex];
    const isManualStep = !!currentStep && !!currentStep.disableScrolling;

    return (
        <Joyride
            key={stepIndex}
            steps={steps}
            run={run}
            stepIndex={stepIndex}
            continuous={true}
            showProgress={true}
            showSkipButton={true}
            disableScrolling={isManualStep}
            scrollToFirstStep={!isManualStep}
            spotlightClicks={true}
            callback={handleCallback}
            scrollOffset={400}
            getHelpers={(helpers) => (helpersRef.current = helpers)}
            tooltipComponent={(props) => <div>{props.step.content}</div>}
            styles={{
                spotlight: {
                    borderRadius: "8px",
                },
            }}
        />
    );
};

export default WarehouseJoyrideWrapper;
